import React, { useEffect, useMemo, useState } from "react";
import {
  FaEnvelope,
  FaMapMarkerAlt,
  FaPhone,
  FaRoute,
  FaUserTie,
} from "react-icons/fa";

import Reveal from "../../../components/Reveal/Reveal";
import { getSettings } from "../../../services/settingsServices";

const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3317";

const CLUB_DOJOS = [
  {
    id: "royan",
    nom: "Salle Landry",
    ville: "Royan",
    adresse: "Bd de Lattre de Tassigny, 17200 Royan, France",
  },
  {
    id: "saint-palais-sur-mer",
    nom: "Dojo de Saint-Palais-sur-Mer",
    ville: "Saint-Palais-sur-Mer",
    adresse: "13 rue des Écoles, 17420 Saint-Palais-sur-Mer, France",
  },
];

const isExternalURL = (url) => {
  try {
    const newUrl = new URL(url);

    return newUrl.protocol === "http:" || newUrl.protocol === "https:";
  } catch (error) {
    return false;
  }
};

const getImageUrl = (url) => {
  if (!url) return "";

  const cleanUrl = String(url).trim();

  if (!cleanUrl) return "";

  if (
    isExternalURL(cleanUrl) ||
    cleanUrl.startsWith("data:") ||
    cleanUrl.startsWith("blob:")
  ) {
    return cleanUrl;
  }

  const cleanBaseUrl = BASE_URL.replace(/\/$/, "");

  if (cleanUrl.startsWith("/")) {
    return `${cleanBaseUrl}${cleanUrl}`;
  }

  return `${cleanBaseUrl}/${cleanUrl}`;
};

const normalizePhoneHref = (phone = "") => {
  const clean = String(phone).replace(/\s+/g, "");

  return clean ? `tel:${clean}` : "";
};

const ContactItem = ({ icon: Icon, label, value, href }) => {
  if (!value) return null;

  const content = (
    <div className="flex items-start gap-4 rounded-[1.5rem] border border-slate-200 bg-white/80 p-4 shadow-sm transition hover:border-orange-200 hover:bg-orange-50/50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10">
      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
        <Icon />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
          {label}
        </p>

        <p className="mt-1 break-words text-sm font-semibold leading-6 text-slate-700 dark:text-slate-200">
          {value}
        </p>
      </div>
    </div>
  );

  if (!href) return content;

  return (
    <a href={href} className="block">
      {content}
    </a>
  );
};

export default function DojoContacts() {
  const [settings, setSettings] = useState({
    dojoPhotoUrl: "",
    adresse: "",
    responsable: "",
    tel: "",
    mail: "",
    nom_club: "",
  });

  const [activeDojoId, setActiveDojoId] = useState("royan");

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const clubSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !clubSettings) return;

        setSettings({
          dojoPhotoUrl: clubSettings.dojo_photo_url || "",

          adresse:
            clubSettings.adresse_club ||
            clubSettings.address ||
            [
              clubSettings.adresse,
              clubSettings.code_postal,
              clubSettings.ville,
            ]
              .filter(Boolean)
              .join(" "),

          responsable: clubSettings.responsable_club || "",

          tel:
            clubSettings.tel_club ||
            clubSettings.telephone_club ||
            clubSettings.phone ||
            "",

          mail:
            clubSettings.mail_club ||
            clubSettings.email_club ||
            clubSettings.email ||
            "",

          nom_club: clubSettings.nom_club || "Le club",
        });
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des paramètres du club :",
          error,
        );
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  /*
   * L’adresse présente dans les paramètres continue d’alimenter
   * le dojo principal de Royan.
   */
  const dojoLocations = useMemo(
    () =>
      CLUB_DOJOS.map((dojo) => {
        if (dojo.id !== "royan") {
          return dojo;
        }

        return {
          ...dojo,
          adresse: settings.adresse || dojo.adresse,
        };
      }),
    [settings.adresse],
  );

  const activeDojo = useMemo(
    () =>
      dojoLocations.find((dojo) => dojo.id === activeDojoId) ||
      dojoLocations[0],
    [dojoLocations, activeDojoId],
  );

  const activeDojoQuery = useMemo(() => {
    if (!activeDojo) return "";

    return `${activeDojo.nom}, ${activeDojo.adresse}`;
  }, [activeDojo]);

  const mapUrl = useMemo(() => {
    if (!activeDojoQuery) return "";

    return `https://www.google.com/maps?q=${encodeURIComponent(
      activeDojoQuery,
    )}&output=embed`;
  }, [activeDojoQuery]);

  const directionsUrl = useMemo(() => {
    if (!activeDojoQuery) return "";

    return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      activeDojoQuery,
    )}`;
  }, [activeDojoQuery]);

  const dojoPhotoUrl = getImageUrl(settings.dojoPhotoUrl);
  const phoneHref = normalizePhoneHref(settings.tel);
  const emailHref = settings.mail ? `mailto:${settings.mail}` : "";

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.12),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.13)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.13)_1px,transparent_1px)] bg-[size:42px_42px] opacity-35 dark:opacity-15" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaMapMarkerAlt />
            Nous trouver
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Dojos & <span className="text-primary">contacts</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Retrouvez les adresses de nos deux dojos, leurs cartes d’accès et
            toutes les coordonnées utiles pour nous contacter.
          </p>
        </Reveal>

        <div className="mt-14 grid items-start gap-8 lg:grid-cols-[1.12fr_0.88fr]">
          {/* Dojos et carte */}
          <Reveal variant="left">
            <div className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white p-3 shadow-2xl dark:border-white/10 dark:bg-slate-900">
              <div className="p-2 sm:p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary">
                      Nos lieux de pratique
                    </p>

                    <h3 className="mt-1 font-barlow text-2xl font-bold text-slate-950 dark:text-white sm:text-3xl">
                      Choisissez un dojo
                    </h3>
                  </div>

                  <span className="w-fit shrink-0 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-xs font-bold text-slate-600 dark:border-white/10 dark:bg-white/10 dark:text-slate-300">
                    {dojoLocations.length} dojos
                  </span>
                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  {dojoLocations.map((dojo) => {
                    const isActive = dojo.id === activeDojo?.id;

                    return (
                      <button
                        key={dojo.id}
                        type="button"
                        onClick={() => setActiveDojoId(dojo.id)}
                        aria-pressed={isActive}
                        className={[
                          "flex min-w-0 items-start gap-3 rounded-[1.5rem] border p-4 text-left transition",
                          isActive
                            ? "border-primary bg-orange-50 shadow-[0_14px_35px_rgba(242,113,49,0.12)] dark:bg-primary/15"
                            : "border-slate-200 bg-slate-50 hover:border-orange-200 hover:bg-orange-50/50 dark:border-white/10 dark:bg-white/5 dark:hover:bg-white/10",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "flex h-11 w-11 shrink-0 items-center justify-center rounded-xl",
                            isActive
                              ? "bg-primary text-white"
                              : "bg-white text-primary shadow-sm dark:bg-white/10",
                          ].join(" ")}
                        >
                          <FaMapMarkerAlt />
                        </span>

                        <span className="min-w-0">
                          <span
                            className={[
                              "block text-[11px] font-bold uppercase tracking-[0.15em]",
                              isActive ? "text-primary" : "text-slate-400",
                            ].join(" ")}
                          >
                            {dojo.ville}
                          </span>

                          <span className="mt-1 block font-barlow text-lg font-bold leading-tight text-slate-950 dark:text-white">
                            {dojo.nom}
                          </span>

                          <span className="mt-2 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                            {dojo.adresse}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="mt-2 overflow-hidden rounded-[2rem] border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-slate-950">
                <div className="flex flex-col gap-3 border-b border-slate-200 bg-white/80 px-5 py-4 dark:border-white/10 dark:bg-white/5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                      Localisation affichée
                    </p>

                    <p className="mt-1 font-barlow text-xl font-bold text-slate-950 dark:text-white">
                      {activeDojo?.nom}
                    </p>

                    <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                      {activeDojo?.adresse}
                    </p>
                  </div>

                  {directionsUrl && (
                    <a
                      href={directionsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex w-fit shrink-0 items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-2.5 text-xs font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
                    >
                      <FaRoute />
                      Voir l’itinéraire
                    </a>
                  )}
                </div>

                {mapUrl ? (
                  <iframe
                    title={`Carte ${activeDojo?.nom || settings.nom_club}`}
                    src={mapUrl}
                    className="block h-[340px] w-full lg:h-[440px]"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                ) : (
                  <div className="flex h-[340px] items-center justify-center px-6 text-center text-sm font-semibold text-slate-500 dark:text-slate-400 lg:h-[440px]">
                    Adresse non renseignée.
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          {/* Présentation et contacts */}
          <Reveal variant="right" delay={120}>
            <div className="flex flex-col gap-6">
              <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 p-6 text-white shadow-2xl sm:p-8">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.32),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.24),transparent_34%)]" />

                <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.08)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:42px_42px] opacity-20" />

                <div className="relative">
                  <div className="flex h-16 w-16 items-center justify-center rounded-[1.4rem] bg-white text-2xl text-primary shadow-xl">
                    <FaRoute />
                  </div>

                  <h3 className="mt-6 font-barlow text-3xl font-bold leading-tight sm:text-4xl">
                    {settings.nom_club}
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    Venez nous rencontrer à Royan ou à Saint-Palais-sur-Mer.
                    Contactez-nous pour toute question concernant les cours,
                    les inscriptions ou la vie du club.
                  </p>
                </div>
              </div>

              {dojoPhotoUrl && (
                <div className="overflow-hidden rounded-[2.5rem] border border-slate-200 bg-white p-3 shadow-xl dark:border-white/10 dark:bg-slate-900">
                  <img
                    src={dojoPhotoUrl}
                    alt={`Dojo ${settings.nom_club}`}
                    className="h-64 w-full rounded-[2rem] object-cover"
                    loading="lazy"
                  />
                </div>
              )}

              <div className="grid gap-4">
                <ContactItem
                  icon={FaUserTie}
                  label="Responsable"
                  value={settings.responsable}
                />

                <ContactItem
                  icon={FaPhone}
                  label="Téléphone"
                  value={settings.tel}
                  href={phoneHref}
                />

                <ContactItem
                  icon={FaEnvelope}
                  label="Email"
                  value={settings.mail}
                  href={emailHref}
                />
              </div>

              {(settings.tel || settings.mail) && (
                <div className="flex flex-col gap-3 sm:flex-row">
                  {settings.tel && (
                    <a
                      href={phoneHref}
                      className="inline-flex flex-1 items-center justify-center gap-3 rounded-full bg-slate-950 px-6 py-3 text-sm font-bold text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-primary dark:bg-white dark:text-slate-950 dark:hover:bg-primary dark:hover:text-white"
                    >
                      <FaPhone />
                      Appeler
                    </a>
                  )}

                  {settings.mail && (
                    <a
                      href={emailHref}
                      className="inline-flex flex-1 items-center justify-center gap-3 rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:-translate-y-0.5 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 dark:border-white/10 dark:bg-white/5 dark:text-white dark:hover:bg-white/10"
                    >
                      <FaEnvelope />
                      Envoyer un mail
                    </a>
                  )}
                </div>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}